import {titleize, singularize, pluralize} from 'inflected'
import _ from 'lodash'
import {CrudCollection, CrudAccessGroup} from '@hc/graphql'
import {Helper, Renderer} from './helpers'
import {ColDef} from '@ag-grid-community/all-modules'
import {formatCurrency} from '@hc/graphql'

export function useGridColumns(collection: CrudCollection, access: CrudAccessGroup){
  const formSchema = collection.formSchema
  const cols = []
  const rowsSortable = collection.fetchFields.indexOf('sort_index') >= 0
  cols.push({
    headerName: 'Id',
    field: 'id',
    pinned: 'left',
    width: rowsSortable ? 110 : 90,
    filter: 'agNumberColumnFilter',
    filterParams: {newRowsAction: 'keep'},
    editable: false,
    rowDrag: rowsSortable,
    checkboxSelection: Helper.checkboxSelection,
    headerCheckboxSelection: Helper.checkboxSelection,
    pinnedRowCellRenderer: Renderer.empty,
  })
  for(const field of collection.listFields) {
    if(collection.listDisplayFieldFilter && !collection.listDisplayFieldFilter(field, access)){
      // field hidden
      continue
    }
    const form = formSchema ? formSchema.fields[field] : null
    const relation = collection.relation(field)
    const join = collection.join(field)
    const fieldCol: ColDef = {
      headerName: titleize(field),
      field: field,
      editable: false,
      checkboxSelection: Helper.checkboxSelection,
      headerCheckboxSelection: Helper.checkboxSelection,
      pinnedRowCellRenderer: Renderer.empty,
    }
    if(field == 'sort_index') continue
    if(relation){
      fieldCol.valueGetter = (row) => {
        if(!row.data || !row.data[field]) return []
        return row.data[field].map((row) => {
          return relation.objectTitle(row, collection.name)
        })
      }
    }
    else if(join) {
      //console.log("JOIN", field, join)
      fieldCol.valueGetter = (row) => row.data && row.data[field] && join.objectTitle(row.data[field])
    } else if(form) {
      //console.log("FORM", field, form.type)
      const formMeta = collection.formSchema.fieldMeta(field)
      if(formMeta.label) fieldCol.headerName = formMeta.label
      if(formMeta.columnWidth) fieldCol.width = formMeta.columnWidth
      switch(form.type) {
        case 'number':
          //console.log("NUMBER", field)
          fieldCol.filter = 'agNumberColumnFilter'
          fieldCol.aggFunc = 'sum'
          fieldCol.pinnedRowCellRenderer = (row) => row.value.value
          break
        case 'price':
          fieldCol.filter = 'agNumberColumnFilter'
          fieldCol.aggFunc = 'sum'
          fieldCol.type = 'numericColumn'
          fieldCol.cellRenderer = (row) => {
            if(!row || row.value == null) return
            const value = row.value
            const currency = _.get(row.data, collection.formMeta.currencySource)
            return formatCurrency(row.value, _.get(row.data, collection.formMeta.currencySource))
          }
          fieldCol.pinnedRowCellRenderer = (row) => {
            if(!row || row.value == null) return
            const sum = row.value
            return formatCurrency(sum.value, 'EUR')
          }
          break
        case 'date':
          fieldCol.cellRenderer = Renderer.date
          fieldCol.filter = 'agDateColumnFilter'
          fieldCol.filterParams = {
            // provide comparator function
            comparator: function (filterLocalDateAtMidnight, cellValue) {
              if (cellValue == null) return 0
              const dateParts = cellValue.split('-')
              const day = Number(dateParts[2])
              const month = Number(dateParts[1]) - 1
              const year = Number(dateParts[0])
              const cellDate = new Date(year, month, day)
              if (cellDate < filterLocalDateAtMidnight) {
                return -1
              } else if (cellDate > filterLocalDateAtMidnight) {
                return 1
              } else {
                return 0
              }
            }
          }
          break
        case 'percent':
          fieldCol.type = 'numericColumn'
          fieldCol.cellRenderer = Renderer.percent
          break
        case 'boolean':
          fieldCol.cellRenderer = Renderer.boolean
          break
        case 'enum':
          fieldCol.cellRenderer = (row) => {
            if(!row || row.value == null) return
            if(!formMeta.itemMap) return row.value
            return formMeta.itemMap[row.value]
          }
          break
        default:
          fieldCol.filter = 'agTextColumnFilter'
          break
      }
    } else {
      if(field.endsWith('_aggregate')) {
        //console.log("AGGREGATE", field)
        fieldCol.filter = 'agNumberColumnFilter'
        fieldCol.headerName = fieldCol.headerName.replace('Aggregate', 'Count')
        fieldCol.cellRenderer = Renderer.aggregate
      }
    }
    if(fieldCol.filter){
      fieldCol.filterParams = {
        ...fieldCol.filterParams,
        newRowsAction: 'keep'
      }
    }
    cols.push(fieldCol)
  }

  cols.push({headerName: 'Created At', field: 'created_at', cellRenderer: Renderer.datetime})
  cols.push({headerName: 'Updated At', field: 'updated_at', cellRenderer: Renderer.datetime})

  return cols
}
